// eslint-disable-next-line max-classes-per-file
import { useMemo } from "react";
import ReactQuill, { Quill, Range } from "react-quill";
// eslint-disable-next-line import/no-extraneous-dependencies
import Styles from "./Editor.module.scss";

import EmojiList from "../../emoji-picker/emoji-short.json";

type QuillEditor = InstanceType<(typeof ReactQuill)["Quill"]>;

const STANDARD_REPLACEMENT_SET: Record<string, string> = {
    ":)": EmojiList[":slightly_smiling_face:"],
    ":D": EmojiList[":smile:"],
    ":(": EmojiList[":disappointed:"],
    ":'(": EmojiList[":cry:"],
};

const Module = Quill.import("core/module");

export default function useEmoji() {
    return useMemo(() => ({
        "foqal-emoji": true,
    }), []);
}

function isWhiteSpace(character: string) {
    return /\s/.test(character);
}

const MIN_CHARACTERS = 2;

class ShortNameEmoji extends Module {
    private isOpen = false;

    private lastSelection = 0;

    private container: HTMLUListElement;

    private quillContainer: HTMLElement;

    private focusedButton: number | undefined;

    private selectedButton = 0;

    constructor(private quill: QuillEditor) {
        super(quill);

        this.container = document.createElement("ul");
        this.container.classList.add(Styles["emoji-container"]);

        this.quillContainer = ((this.quill as unknown as Record<string, HTMLElement>).container as HTMLElement);
        this.quillContainer.appendChild(this.container);
        this.container.style.position = "absolute";
        this.container.style.display = "none";

        this.onSelectionChange = this.maybeUnfocus.bind(this);
        this.onTextChange = this.update.bind(this);

        type Key = Parameters<typeof quill.keyboard.addBinding>[0];
        quill.keyboard.addBinding({
            key: 186 as unknown as string,
            shiftKey: true,
        } as Key, this.triggerPicker.bind(this));

        quill.keyboard.addBinding({
            key: 38 as unknown as string,
        }, this.handleArrowUp.bind(this));

        quill.keyboard.addBinding({
            key: 40 as unknown as string,
        }, this.handleArrowDown.bind(this));

        this.quill.keyboard.addBinding({
            key: 13 as unknown as string,
        } as Key, this.handleSelected?.bind(this));

        this.quill.keyboard.addBinding({
            key: 9 as unknown as string,
        } as Key, this.handleSelected?.bind(this));

        this.quill.keyboard.addBinding({
            key: 32 as unknown as string,
        } as Key, this.handleSelected?.bind(this));
    }

    triggerPicker(range: Exclude<Range, null>) {
        if (this.isOpen) {
            return true;
        }
        if (range && range.length > 0) {
            this.quill.deleteText(range.index, range.length, "user");
        }

        this.quill.insertText(range.index, ":", "emoji-shortname", "user");
        const atSignBounds = this.quill.getBounds(range.index);
        this.quill.setSelection(range.index + 1, 0, "silent");

        this.lastSelection = range.index;

        const paletteMaxPos = atSignBounds.left + 250;
        if (paletteMaxPos > this.quillContainer.offsetWidth) {
            this.container.style.left = `${atSignBounds.left - 250}px`;
        } else {
            this.container.style.left = `${atSignBounds.left}px`;
        }

        this.container.style.top = `${atSignBounds.top + atSignBounds.height}px`;
        this.isOpen = true;

        this.quill.on("text-change", this.onTextChange);
        this.quill.once("selection-change", this.onSelectionChange);

        return false;
    }

    handleArrowUp() {
        if (!this.isOpen || !this.buttons) {
            return true;
        }
        this.buttons[this.selectedButton].classList.remove("active");
        this.selectedButton = this.selectedButton === 0 ? this.buttons.length - 1 : this.selectedButton - 1;
        this.buttons[this.selectedButton].classList.add("active");
        this.buttons[this.selectedButton].focus();
        return false;
    }

    handleArrowDown() {
        if (!this.isOpen || !this.buttons) {
            return true;
        }
        this.buttons[this.selectedButton].classList.remove("active");
        this.selectedButton = (this.selectedButton + 1) % this.buttons.length;
        this.buttons[this.selectedButton].classList.add("active");
        this.buttons[this.selectedButton].focus();
        return false;
    }

    handleSelected() {
        if (!this.isOpen || !this.buttons) {
            return true;
        }
        const selected = this.buttons[this.selectedButton].dataset.emoji;
        this.close(selected);
        return false;
    }

    update() {
        const selection = this.quill.getSelection()?.index ?? 0;
        if (this.lastSelection >= selection) {
            this.close();
            return true;
        }
        this.query = this.quill.getText(this.lastSelection + 1, selection - this.lastSelection - 1);

        try {
            if (isWhiteSpace(this.query)) {
                this.close();
                return true;
            }
        } catch (error) {
            //
        }

        this.query = this.query.trim();

        const standard = STANDARD_REPLACEMENT_SET[`:${this.query}`];
        if (standard) {
            this.close(standard, 1);
            return true;
        }

        if (this.query.length < MIN_CHARACTERS) {
            this.hide();
            return true;
        }
        const emojis = Object.keys(EmojiList).filter(emoji => emoji.includes(this.query)).limit(10);
        if (emojis.length === 0) {
            this.hide();
            return true;
        }
        this.renderCompletions(emojis);

        return false;
    }

    private hide() {
        this.container.style.display = "none";
    }

    private maybeUnfocus() {
        if (this.container.querySelector("*:focus")) {
            return;
        }
        this.close();
    }

    renderCompletions(emojis: string[]) {
        this.clearContainer();

        const createHandler = (index: number, emoji: string) => (event: KeyboardEvent) => {
            if (event.key === "ArrowDown") {
                event.preventDefault();
                this.handleArrowDown();
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                this.handleArrowUp();
            } else if (event.key === "Tab" || event.key === "Enter" || event.key === " " || event.key === "Tab") {
                event.preventDefault();
                this.close(emoji);
            }
        };

        this.buttons = emojis.map((emoji, index) => {
            const li = document.createElement("li");
            const button = document.createElement("button");
            button.type = "button";
            button.classList.add(Styles["emoji-button"]);
            li.appendChild(button);

            const emojiValue = EmojiList[emoji as keyof typeof EmojiList];
            button.textContent = `${emojiValue} ${emoji}`;
            button.dataset.emoji = emojiValue;

            this.container.appendChild(li);
            button.addEventListener("keydown", createHandler(index, emojiValue));
            button.addEventListener("mousedown", () => this.close(emojiValue));
            button.addEventListener("focus", () => {
                this.selectedButton = index;
            });
            button.addEventListener("unfocus", () => {
                this.selectedButton = 0;
            });
            return button;
        });

        this.container.style.display = "block";
        this.selectedButton = 0;
        if (this.buttons.isNotEmpty) {
            this.buttons[0].classList.add("active");
            this.buttons[0].focus();
        }
    }

    private clearContainer() {
        while (this.container.firstChild) {
            this.container.removeChild(this.container.firstChild);
        }
    }

    close(value?: string, trailingDelete = 0) {
        this.quill.enable();
        this.container.style.display = "none";
        this.clearContainer();
        this.quill.off("selection-change", this.onSelectionChange);
        this.quill.off("text-change", this.onTextChange);
        if (value) {
            this.quill.deleteText(this.lastSelection ?? 0, this.query.length + 1 + trailingDelete, "user");
            this.quill.insertEmbed(this.lastSelection ?? 0, "foqal-emoji", value, "user");
            setTimeout(() => this.quill.setSelection(this.lastSelection + 2, 0, "silent"));
        }
        this.quill.focus();
        this.isOpen = false;
    }
}

// eslint-disable-next-line import/no-extraneous-dependencies
Quill.register("modules/foqal-emoji", ShortNameEmoji);

const Inline = Quill.import("blots/inline");

class EmojiBlot extends Inline {
    static create(value: string) {
        const node = super.create() as HTMLSpanElement;
        const span = document.createElement("span");
        node.appendChild(span);
        span.setAttribute("data-emoji", value);
        span.contentEditable = "false";
        span.textContent = value;
        return node;
    }

    static value(node: HTMLElement) {
        return node.dataset.name;
    }
}

EmojiBlot.blotName = "foqal-emoji";
EmojiBlot.className = Styles["emoji-blot"];
EmojiBlot.tagName = "span";
EmojiBlot.emojiClass = "ap";
EmojiBlot.emojiPrefix = "ap-";

Quill.register("formats/foqal-emoji", EmojiBlot);
